/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  type ReactNode,
  type FC,
  type Ref,
} from "react";
import mapboxgl from "mapbox-gl";
import {
  lightColorPalette,
  darkColorPalette,
  type ClassNameProp,
  Select,
  Icon,
  Message,
  useDelayedState,
  nextFrame,
  type SelectListValue,
  type SelectListOption,
  type SelectChangeHandler,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import { ZoomControls } from "./ZoomControls";
import { MemoizedHotspotController } from "./MemoizedHotspotController";
import type { HotspotRecord } from "../utils/mapMarkers";
import { getMapboxStyleUrl, type MapBoxMapMode } from "../utils/mapStyles";
import { createZoomHandlers } from "../utils/mapControls";
import {
  add3DBuildings,
  addGeoJSONLayers,
  removeGeoJSONLayers,
  removeSymbolLayers,
  type GetTooltipContentHandler,
  type GetGeoJSONFeatureOpacityHandler,
} from "../utils/mapLayers";
import { calculateViewportRadius } from "../utils/mapCalculations";
import { isWebGLSupported } from "../utils/isWebGLSupported";

import "mapbox-gl/dist/mapbox-gl.css";
import {
  controlsContainerStyle,
  mapContainerStyle,
  mapWrapperStyle,
  layerSelectStyle,
  leadingSlotStyle,
  closeSlotStyle,
  mapToolsStyle,
} from "../MapboxMapView.css";

/**
 * Performance configuration type
 */
export type MapBoxPerformanceConfig = {
  /** Disable anti-aliasing for better performance (default: false) */
  antialias?: boolean;
  /** Preserve drawing buffer for screenshots (disable for performance, default: false) */
  preserveDrawingBuffer?: boolean;
  /** Refresh expired tiles (disable to reduce tile reloading, default: true) */
  refreshExpiredTiles?: boolean;
};

/**
 * Default performance configuration constants
 * Adjust these values to balance performance vs visual quality
 */
const DEFAULT_PERFORMANCE_CONFIG: Required<MapBoxPerformanceConfig> = {
  antialias: false,
  preserveDrawingBuffer: false,
  refreshExpiredTiles: true,
} as const;

export type MapBoxAnimationConfig = {
  /**
   * Target zoom level for animation (default: 16)
   **/
  zoomLevel?: number;
  /**
   * Animation duration in milliseconds (default: 1500)
   **/
  duration?: number;
  /**
   * Target pitch angle for animation (0-60 degrees)
   **/
  pitch?: number;
  /**
   * Target center coordinates [longitude, latitude] for animation
   **/
  center?: [number, number];
};

export type MapBoxThreeDConfig = {
  /**
   * Enable 3D building extrusions (default: false)
   **/
  enabled?: boolean;
  /**
   * Color of the 3D buildings (default: "#CCCCCC")
   **/
  buildingColor?: string;
  /**
   * Opacity of the 3D buildings (0-1, default: 1)
   **/
  opacity?: number;
  /**
   * Layer ID to insert before (optional)
   **/
  beforeLayerId?: string;
};

/**
 * Map viewport data structure
 */
export type MapViewport = {
  /**
   * Center coordinates [longitude, latitude]
   **/
  center: [number, number];
  /**
   * Radius in meters from center to edge of viewport
   **/
  radius: number;
};

/**
 * Options for adding hotspots with clustering
 */
export type AddHotspotsOptions = {
  /**
   * Maximum number of hotspots before clustering (default: 50)
   **/
  clusterThreshold?: number;
  /**
   * Radius in meters for clustering nearby hotspots (default: 100)
   **/
  clusterRadius?: number;
  /**
   * Replace existing hotspots instead of merging (default: false)
   **/
  replace?: boolean;
};

/**
 * Map camera animation options for flyTo
 */
export type FlyToOptions = {
  center?: [number, number];
  zoom?: number;
  pitch?: number;
  bearing?: number;
  duration?: number;
};

/**
 * Map camera animation options for easeTo
 */
export type EaseToOptions = {
  center?: [number, number];
  zoom?: number;
  pitch?: number;
  bearing?: number;
  duration?: number;
};

/**
 * Fit bounds options
 */
export type FitBoundsOptions = {
  padding?:
    | number
    | { top: number; bottom: number; left: number; right: number };
  duration?: number;
  maxZoom?: number;
};

/**
 * Imperative handle for MapboxMapView
 * Exposes methods to control the map instance
 */
export type MapboxMapViewHandle = {
  /**
   * Animate to location with smooth fly animation
   */
  flyTo: (options: FlyToOptions) => void;
  /**
   * Ease animation to location
   */
  easeTo: (options: EaseToOptions) => void;
  /**
   * Set map center
   */
  setCenter: (coords: [number, number]) => void;
  /**
   * Set zoom level
   */
  setZoom: (level: number) => void;
  /**
   * Get current center coordinates
   */
  getCenter: () => [number, number] | null;
  /**
   * Get current zoom level
   */
  getZoom: () => number | null;
  /**
   * Get current map bounds
   */
  getBounds: () => any | null;
  /**
   * Fit map to bounds
   */
  fitBounds: (
    bounds: [[number, number], [number, number]],
    options?: FitBoundsOptions,
  ) => void;
  /**
   * Get raw Mapbox map instance
   */
  getMap: () => mapboxgl.Map | null;
};

export type DynamicMapboxViewProps = ClassNameProp & {
  /**
   * Map interaction type - prepares for future animation support
   **/
  type?: "animated" | "interactive";
  /**
   * Array of hotspots to display on the map
   **/
  hotspots?: HotspotRecord[];
  /**
   * Center coordinates [longitude, latitude]
   **/
  center: [number, number];
  /**
   * Zoom level (default: 14)
   **/
  zoom?: number;
  /**
   * Pitch angle (0-60 degrees, default: 0)
   **/
  pitch?: number;
  /**
   * Bearing angle (0-360 degrees, default: 0)
   **/
  bearing?: number;
  /**
   * 3D buildings configuration (use buildingColor to customize color)
   **/
  threeDConfig?: MapBoxThreeDConfig;
  /**
   * Hide all labels (street names, place names, etc.)
   **/
  hideLabels?: boolean;
  /**
   * Optional GeoJSON data to display
   * */
  geoJSON?: GeoJSON.FeatureCollection;
  /**
   * Callback to generate tooltip content for GeoJSON features
   * Return null to hide tooltip for a feature
   **/
  getTooltipContent?: GetTooltipContentHandler;
  /**
   * Callback to generate opacity for each GeoJSON feature
   * Return a number between 0 and 1, or null to use default opacity
   **/
  getGeoJSONFeatureOpacity?: GetGeoJSONFeatureOpacityHandler;
  /**
   * Mapbox access token
   **/
  accessToken: string;
  /**
   * Map mode (default: "light")
   **/
  mode?: MapBoxMapMode;
  /**
   * Configuration for map controls
   * Set to false to hide all controls
   **/
  controlsConfig?:
    | false
    | {
        zoom?: boolean;
        mode?: boolean;
        scrollZoom?: boolean;
      };
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<MapboxMapViewHandle>;
  /**
   * Performance configuration for map rendering
   **/
  performanceConfig?: MapBoxPerformanceConfig;
  /**
   * Animation configuration for animated maps
   **/
  animationConfig?: MapBoxAnimationConfig;
  /**
   * Custom controls to display in the leading position of the controls container
   **/
  customControlsLeading?: ReactNode;
  /**
   * Custom controls to display in the trailing position of the controls container
   **/
  customControlsTrailing?: ReactNode;
  /**
   * Callback when map mode changes
   **/
  onModeChange?: (mode: MapBoxMapMode) => void;
  /**
   * Callback function to execute when the map is loaded
   **/
  onLoad?: () => void;
  /**
   * Callback function to execute when map, styles, and all layers are fully ready
   **/
  onReady?: () => void;
  /**
   * Callback function to execute when style is loaded
   **/
  onStyleLoad?: () => void;
  /**
   * Callback function to execute when viewport changes (pan/zoom)
   * Provides center coordinates and radius in meters
   **/
  onViewportChange?: (viewport: MapViewport) => void;
};

/**
 * DynamicMapboxView component for displaying a Mapbox map
 * Dynamically loads Mapbox GL to avoid SSR issues
 * @todo Could remove async loading of Mapbox module
 */
export const DynamicMapboxView: FC<DynamicMapboxViewProps> = ({
  center,
  zoom = 14,
  pitch = 0,
  bearing = 0,
  threeDConfig,
  hideLabels = false,
  geoJSON,
  accessToken,
  mode = "light",
  type = "interactive",
  animationConfig = { zoomLevel: 16, duration: 1500 },
  hotspots,
  controlsConfig = { zoom: true, mode: true, scrollZoom: false },
  performanceConfig,
  customControlsLeading,
  customControlsTrailing,
  getTooltipContent,
  getGeoJSONFeatureOpacity,
  onModeChange,
  onLoad,
  onReady,
  onStyleLoad,
  onViewportChange,
  className,
  ref,
}) => {
  const finalPerformanceConfig = {
    ...DEFAULT_PERFORMANCE_CONFIG,
    ...performanceConfig,
  };

  const webGLSupported = isWebGLSupported();
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const [layersReady, setLayersReady] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [mapMode, setMapMode] = useState<MapBoxMapMode>(mode);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const onReadyCalledRef = useRef(false);
  const onViewportChangeCalledRef = useRef(false);
  const onViewportChangeRef = useRef(onViewportChange);
  const geoJSONRef = useRef(geoJSON);
  const getTooltipContentRef = useRef(getTooltipContent);
  const getGeoJSONFeatureOpacityRef = useRef(getGeoJSONFeatureOpacity);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);

  // Keep geoJSON ref updated for use in mapMode effect
  useEffect(() => {
    geoJSONRef.current = geoJSON;
  }, [geoJSON]);

  // Keep getTooltipContent ref updated for use in mapMode effect
  useEffect(() => {
    getTooltipContentRef.current = getTooltipContent;
  }, [getTooltipContent]);

  // Keep getGeoJSONFeatureOpacity ref updated for use in mapMode effect
  useEffect(() => {
    getGeoJSONFeatureOpacityRef.current = getGeoJSONFeatureOpacity;
  }, [getGeoJSONFeatureOpacity]);

  // Expose imperative handle methods
  useImperativeHandle(
    ref,
    () => ({
      flyTo: (options: FlyToOptions) => {
        if (!mapInstanceRef.current) return;
        mapInstanceRef.current.flyTo(options);
      },
      easeTo: (options: EaseToOptions) => {
        if (!mapInstanceRef.current) return;
        mapInstanceRef.current.easeTo(options);
      },
      setCenter: (coords: [number, number]) => {
        if (!mapInstanceRef.current) return;
        mapInstanceRef.current.setCenter(coords);
      },
      setZoom: (level: number) => {
        if (!mapInstanceRef.current) return;
        mapInstanceRef.current.setZoom(level);
      },
      getCenter: (): [number, number] | null => {
        if (!mapInstanceRef.current) return null;
        const center = mapInstanceRef.current.getCenter();
        return [center.lng, center.lat];
      },
      getZoom: (): number | null => {
        if (!mapInstanceRef.current) return null;
        return mapInstanceRef.current.getZoom();
      },
      getBounds: (): any | null => {
        if (!mapInstanceRef.current) return null;
        return mapInstanceRef.current.getBounds();
      },
      fitBounds: (
        bounds: [[number, number], [number, number]],
        options?: FitBoundsOptions,
      ) => {
        if (!mapInstanceRef.current) return;
        mapInstanceRef.current.fitBounds(bounds, options);
      },
      getMap: (): mapboxgl.Map | null => mapInstanceRef.current,
      triggerRepaint: () => {
        if (!mapInstanceRef.current) return;
        mapInstanceRef.current.triggerRepaint();
      },
    }),
    [],
  );

  /**
   * Set isReady state when map, styles, and layers are all ready
   */
  useEffect(() => {
    if (mapLoaded && styleLoaded && layersReady) {
      setIsReady(true);
    } else {
      setIsReady(false);
    }
  }, [mapLoaded, styleLoaded, layersReady]);

  /**
   * Fire onReady callback when map, styles, and layers are all ready
   */
  useEffect(() => {
    if (isReady && onReady && !onReadyCalledRef.current) {
      // console.log("Map is fully ready - firing onReady callback");
      onReadyCalledRef.current = true;
      onReady();
    }
  }, [isReady, onReady]);

  /**
   * Fire initial viewport change callback when map is ready
   */
  useEffect(() => {
    if (
      isReady &&
      mapInstanceRef.current &&
      onViewportChangeRef.current &&
      !onViewportChangeCalledRef.current
    ) {
      onViewportChangeCalledRef.current = true;
      const map = mapInstanceRef.current;
      const center = map.getCenter();
      const radius = calculateViewportRadius(map);

      onViewportChangeRef.current({
        center: [center.lng, center.lat],
        radius,
      });
    }
  }, [isReady]);

  /**
   * Animate to configured zoom level when map is ready and type is animated
   */
  useEffect(() => {
    // console.log("Animation effect triggered", {
    //   isReady,
    //   type,
    //   hasMapInstance: !!mapInstanceRef.current,
    // });

    if (isReady && type === "animated" && mapInstanceRef.current) {
      const targetZoom = animationConfig.zoomLevel ?? 16;
      const animDuration = animationConfig.duration ?? 1500;
      const targetPitch = animationConfig.pitch;

      // Use config center if provided, otherwise fall back to first hotspot
      // Add slight northward offset for better centering in hero
      const targetCenter = animationConfig.center
        ? animationConfig.center
        : undefined;

      // console.log(
      //   `Animating to zoom level ${targetZoom} over ${animDuration}ms`,
      //   targetCenter
      //     ? `at coordinates [${targetCenter[0]}, ${targetCenter[1]}]`
      //     : "",
      //   targetPitch !== undefined ? `with pitch ${targetPitch}` : "",
      // );

      const map = mapInstanceRef.current;

      // Listen to moveend event as a fallback
      const handleMoveEnd = () => {
        // console.log("Map moveend event fired (animation likely complete)");
        map.off("moveend", handleMoveEnd);
      };

      map.once("moveend", handleMoveEnd);

      map.easeTo({
        center: targetCenter,
        zoom: targetZoom,
        pitch: targetPitch,
        duration: animDuration,
        easing: (t: number) => t * (2 - t), // easeOutQuad
      });

      // Set a timeout to verify animation should be complete
      setTimeout(() => {
        // console.log("Animation timeout complete (fallback check)");
      }, animDuration + 100);
    }
  }, [isReady, type, animationConfig]);

  const { handleZoomIn, handleZoomOut } = createZoomHandlers(mapInstanceRef);

  /**
   * Track viewport changes (pan/zoom)
   */
  useEffect(() => {
    if (!isReady || !mapInstanceRef.current || !onViewportChangeRef.current) {
      return;
    }

    const map = mapInstanceRef.current;

    const handleViewportChange = () => {
      if (onViewportChangeRef.current) {
        const center = map.getCenter();
        const radius = calculateViewportRadius(map);

        onViewportChangeRef.current({
          center: [center.lng, center.lat],
          radius,
        });
      }
    };

    map.on("moveend", handleViewportChange);

    return () => {
      map.off("moveend", handleViewportChange);
    };
  }, [isReady]);

  useEffect(() => {
    if (hasBeenVisible || !mapContainerRef.current) return;

    const el = mapContainerRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.01 },
    );
    observer.observe(el);

    return () => observer.disconnect();
  }, [hasBeenVisible]);

  // Initialize map once
  useEffect(() => {
    if (!hasBeenVisible || !mapContainerRef.current) {
      return;
    }

    // Set Mapbox access token
    mapboxgl.accessToken = accessToken;

    const isAnimated = type === "animated";

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: getMapboxStyleUrl(mode),
      center: [center[1], center[0]],
      zoom,
      pitch,
      maxZoom: 18,
      minZoom: 12,
      bearing,
      dragPan: !isAnimated,
      dragRotate: !isAnimated,
      scrollZoom:
        !isAnimated && controlsConfig !== false && controlsConfig.scrollZoom,
      touchZoomRotate: !isAnimated,
      doubleClickZoom: !isAnimated,
      // Performance optimizations
      antialias: finalPerformanceConfig.antialias,
      preserveDrawingBuffer: finalPerformanceConfig.preserveDrawingBuffer,
      refreshExpiredTiles: finalPerformanceConfig.refreshExpiredTiles,
    });

    // Handle map load event
    map.once("load", () => {
      // console.log("Map load event fired");
      mapInstanceRef.current = map;

      // Hide labels if hideLabels is true
      if (hideLabels) {
        removeSymbolLayers(map);
      }

      // Add 3D buildings if threedConfig is enabled
      if (threeDConfig?.enabled) {
        add3DBuildings(map, {
          color: threeDConfig.buildingColor,
          opacity: threeDConfig.opacity,
          beforeLayerId: threeDConfig.beforeLayerId,
        });
      }

      // GeoJSON is NOT added here — the geoJSON useEffect (below)
      // handles it once mapLoaded flips to true. Adding it here AND
      // in the effect causes a visible blink (remove-then-re-add).

      // Wait for next frame to ensure marker elements are in the DOM
      // before setting ready states
      nextFrame(() => {
        // Set mapLoaded to true after all markers are created
        setMapLoaded(true);
        // Fire onLoad callback when map is loaded
        onLoad?.();

        // Mark style as loaded and layers as ready after initial setup
        setStyleLoaded(true);
        onStyleLoad?.();

        // Mark layers as ready
        setLayersReady(true);
      });
    });

    return () => {
      try {
        if (map && !map._removed) {
          map.remove();
        }
      } catch (error) {
        console.error("Error removing map:", error);
      }
    };
  }, [
    hasBeenVisible,
    center,
    zoom,
    mode,
    pitch,
    bearing,
    type,
    hideLabels,
    threeDConfig,
    accessToken,
    onLoad,
    onStyleLoad,
  ]);

  // Update map style when mapMode changes
  useEffect(() => {
    if (!mapInstanceRef.current) {
      return;
    }

    const map = mapInstanceRef.current;
    const currentMapStyle = getMapboxStyleUrl(mapMode);

    // Reset ready states when changing style
    setStyleLoaded(false);
    setLayersReady(false);

    map.setStyle(currentMapStyle);

    // Re-apply labels, 3D buildings, and GeoJSON after style loads
    map.once("style.load", () => {
      // console.log("Style changed, re-applying settings");
      // TODO: Duplicate logic, needs refactoring

      // Hide labels if hideLabels is true
      if (hideLabels) {
        removeSymbolLayers(map);
      }

      // Add 3D buildings if threedConfig is enabled
      if (threeDConfig?.enabled) {
        add3DBuildings(map, {
          color: threeDConfig.buildingColor,
          opacity: threeDConfig.opacity,
          beforeLayerId: threeDConfig.beforeLayerId,
        });
      }

      // Re-add GeoJSON layers if geoJSON exists (use ref to get current value)
      if (geoJSONRef.current) {
        const accentColor =
          mapMode === "dark"
            ? darkColorPalette.accent
            : lightColorPalette.accent;
        addGeoJSONLayers(
          map,
          geoJSONRef.current,
          accentColor,
          getTooltipContentRef.current,
          getGeoJSONFeatureOpacityRef.current,
        );
      }

      // Fire onStyleLoad callback when style is loaded
      onStyleLoad?.();

      // Wait for next frame to ensure all updates are in the DOM
      requestAnimationFrame(() => {
        // Mark style and layers as ready after style change
        setStyleLoaded(true);
        setLayersReady(true);
        // console.log("Style change complete - style and layers ready");
      });
    });
  }, [mapMode, hideLabels, threeDConfig, onStyleLoad]);

  /**
   * Handle GeoJSON layer updates when geoJSON prop changes or map loads
   */
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) {
      return;
    }

    const map = mapInstanceRef.current;

    // Handle removal when geoJSON becomes undefined
    if (!geoJSON) {
      if (map.isStyleLoaded()) {
        removeGeoJSONLayers(map);
      }
      return;
    }

    const addGeoJSONLayersToMap = () => {
      const accentColor =
        mapMode === "dark" ? darkColorPalette.accent : lightColorPalette.accent;
      addGeoJSONLayers(
        map,
        geoJSON,
        accentColor,
        getTooltipContent,
        getGeoJSONFeatureOpacity,
      );
    };

    // Wait for style to be fully loaded before adding layers
    if (map.isStyleLoaded()) {
      addGeoJSONLayersToMap();
    } else {
      const handleStyleLoad = () => {
        addGeoJSONLayersToMap();
      };
      map.once("style.load", handleStyleLoad);

      return () => {
        map.off("style.load", handleStyleLoad);
      };
    }
  }, [geoJSON, mapMode, mapLoaded]);

  const [areHotspotsVisible, setAreHotspotsVisible] = useDelayedState(
    false,
    500,
  );

  useEffect(() => {
    if (isReady && mapLoaded && hotspots && !areHotspotsVisible) {
      setAreHotspotsVisible(true);
    }
  }, [areHotspotsVisible, mapLoaded, isReady, hotspots]);

  /**
   * Handle layer change
   */
  const handleLayerChange: SelectChangeHandler<MapBoxMapMode> = (mode) => {
    setMapMode(mode);
    onModeChange?.(mode);
  };

  const mapModeOptions: SelectListOption[] = [
    { label: "Straten", value: "streets" },
    { label: "Satelliet", value: "satellite" },
    { label: "Licht", value: "light" },
    { label: "Donker", value: "dark" },
  ];

  // Render fallback message when WebGL is not supported
  if (!webGLSupported) {
    return (
      <div className={mergeClasses(mapWrapperStyle(), className)}>
        <Message
          variant="warning"
          body="Je browser ondersteunt geen WebGL, wat nodig is om de interactieve kaart weer te geven."
        />
      </div>
    );
  }

  return (
    <div
      className={mergeClasses(mapWrapperStyle(), "mapbox-view", className)}
      data-type={type}
    >
      <div ref={mapContainerRef} className={mergeClasses(mapContainerStyle)} />

      {type !== "animated" &&
        controlsConfig !== false &&
        (controlsConfig.zoom || controlsConfig.mode) && (
          <div className={controlsContainerStyle}>
            {customControlsLeading && (
              <div className={leadingSlotStyle}>{customControlsLeading}</div>
            )}
            <div className={mapToolsStyle}>
              {controlsConfig.mode && (
                <Select
                  value={mapMode}
                  className={layerSelectStyle}
                  leading={
                    <Icon
                      icon={
                        <>
                          <path
                            d="M9.60573 4.81298C10.7856 4.27099 11.3755 4 12 4C12.6245 4 13.2144 4.27099 14.3943 4.81298L19.2873 7.06064C21.0958 7.89137 22 8.30674 22 9C22 9.69326 21.0958 10.1086 19.2873 10.9394L14.3943 13.187C13.2144 13.729 12.6245 14 12 14C11.3755 14 10.7856 13.729 9.60573 13.187L4.7127 10.9394C2.90423 10.1086 2 9.69326 2 9C2 8.30674 2.90423 7.89137 4.7127 7.06064L9.60573 4.81298Z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M20.2327 13.5C21.4109 14.062 22 14.4405 22 15.0001C22 15.6934 21.0958 16.1087 19.2873 16.9395L14.3943 19.1871C13.2144 19.7291 12.6245 20.0001 12 20.0001C11.3755 20.0001 10.7856 19.7291 9.60573 19.1871L4.7127 16.9395C2.90423 16.1087 2 15.6934 2 15.0001C2 14.4405 2.58909 14.062 3.76727 13.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </>
                      }
                      size="s"
                    />
                  }
                  onChange={
                    handleLayerChange as SelectChangeHandler<SelectListValue>
                  }
                  display={null}
                  options={mapModeOptions}
                  size="s"
                />
              )}
              {controlsConfig.zoom && (
                <ZoomControls
                  handleZoomIn={handleZoomIn}
                  handleZoomOut={handleZoomOut}
                />
              )}
            </div>
            {customControlsTrailing && (
              <div className={closeSlotStyle}>{customControlsTrailing}</div>
            )}
          </div>
        )}

      {areHotspotsVisible &&
        hotspots &&
        hotspots.map((hotspot) => (
          <MemoizedHotspotController
            key={hotspot.id}
            hotspot={hotspot}
            mapboxgl={mapboxgl}
            mapInstanceRef={mapInstanceRef}
          />
        ))}
    </div>
  );
};
